/**
 * Shared styled components for Admin Portal content pages.
 *
 * These primitives provide consistent admin table layouts, headers, action
 * button grouping, tabs, and expandable detail rows across Dictionary,
 * Physicians, and other admin modules.
 *
 * Page-specific sizing (max-width, min-height, etc.) should be applied via
 * styled(SharedComponent) overrides in each consumer.
 */
import { Button, Tabs, Typography } from "antd";
import styled from "styled-components";

/* ---------- Layout wrappers ---------- */

export const StyledComponentWrapper = styled.div`
  background-color: #ffffff;
  width: 100%;
  padding: 24px 28px;
`;

export const StyledTabWrapper = styled.div`
  width: 100%;
  max-width: 1480px;
  margin: 24px auto;
  @media (max-width: 1800px) {
    max-width: 1300px;
  }
  @media (max-width: 1550px) {
    max-width: 1100px;
  }
  @media (max-width: 1300px) {
    max-width: 950px;
  }
  @media (max-width: 1199px) {
    max-width: 780px;
  }
  @media (max-width: 788px), (max-width: 922px) {
    max-width: 712px;
  }
  @media (max-width: 720px) {
    max-width: 372px;
  }
`;

export const StyledTabs = styled(Tabs)`
  > .ant-tabs-bar {
    margin: unset;
  }
  > .ant-tabs-bar
    > .ant-tabs-nav-container
    > .ant-tabs-nav-wrap
    > .ant-tabs-nav-scroll
    > .ant-tabs-nav
    > div
    > .ant-tabs-tab-active {
    background: #ffffff;
    border-bottom: unset;
  }
`;

/* ---------- Typography ---------- */

export const Title = styled(Typography.Title)`
  && {
    font-size: 36px;
    font-weight: 700;
  }
`;

/* ---------- Table header / controls ---------- */

export const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  ${Title} {
    margin-bottom: 0;
  }
`;

export const TitleControls = styled.span`
  margin-left: 15px;
  position: relative;
  bottom: 4px;
`;

/* ---------- Action buttons ---------- */

export const ActionButtonGroup = styled.div`
  display: inline-flex;
  justify-content: center;
  gap: 8px;
`;

/* ---------- Expandable detail row primitives ---------- */

export const ExpandButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    box-shadow: none;
    background: transparent;
    color: rgba(0, 0, 0, 0.45);

    &:hover,
    &:focus {
      border: 0;
      box-shadow: none;
      background: rgba(0, 0, 0, 0.04);
      color: rgba(0, 0, 0, 0.85);
    }
  }
`;

export const ExpandedDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px 24px;
  padding: 8px 16px;
`;

export const DetailItem = styled.div`
  min-width: 0;
`;

export const DetailLabel = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  margin-bottom: 4px;
`;

export const DetailValue = styled.div`
  color: rgba(0, 0, 0, 0.85);
  overflow-wrap: anywhere;
`;
